import { cva, type VariantProps } from 'class-variance-authority';
import { Slot as SlotPrimitive } from 'radix-ui';
import * as React from 'react';

import { Icons, type LBIcon } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { Tooltip, type TooltipProps } from './Tooltip';

const variantSize = {
  xs: 'size-6 min-w-6 min-h-6 max-w-6 max-h-6',
  sm: 'size-7 min-w-7 min-h-7 max-w-7 max-h-7',
  default: 'size-8 min-w-8 min-h-8 max-w-8 max-h-8',
  md: 'size-9 min-w-9 min-h-9 max-w-9 max-h-9',
  lg: 'size-10 min-w-10 min-h-10 max-w-10 max-h-10',
  xl: 'size-12 min-w-12 min-h-12 max-w-12 max-h-12',
  '2xl': 'size-14 min-w-14 min-h-14 max-w-14 max-h-14',
};

const iconButtonVariants = cva(
  'inline-flex text-foreground items-center justify-center rounded-md smoothing-default transition-colors disabled:pointer-events-none disabled:opacity-50 focus-ring',
  {
    variants: {
      variant: {
        default: 'bg-surface hover:bg-surface-hover',
        ghost: 'hover:bg-surface-hover',
        secondary: 'hover:bg-secondary-hover',
        outline: 'border border-border hover:bg-surface-hover',
        translucent: 'hover:bg-grayA-2',
      },
      size: variantSize,
    },
    defaultVariants: {
      variant: 'ghost',
      size: 'default',
    },
  }
);

export type IconButtonSize =
  | 'xs'
  | 'sm'
  | 'default'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl';

const useIconSize = (size: IconButtonSize = 'default'): string => {
  const sizeMap = {
    xs: 'size-3.5 min-w-3.5 min-h-3.5',
    sm: 'size-4 min-w-4 min-h-4',
    default: 'size-5 min-w-5 min-h-5',
    md: 'size-5 min-w-5 min-h-5',
    lg: 'size-4 min-w-4 min-h-4',
    xl: 'size-6 min-w-6 min-h-6',
    '2xl': 'size-7 min-w-7 min-h-7',
  } as const;
  return sizeMap[size];
};

interface FrameProps {
  scale?: number;
  tx?: number;
  ty?: number;
}

interface IconFrameProps
  extends VariantProps<typeof iconButtonVariants>, FrameProps {
  icon: LBIcon;
  size?: IconButtonSize;
  className?: string;
  strokeWidth?: number;
}

const IconFrame = ({
  icon: Icon,
  size = 'default',
  className,
  scale = 1,
  tx = 0,
  ty = 0,
  strokeWidth = 1.3,
}: IconFrameProps) => {
  const iconSize = useIconSize(size);
  const containerClassName = cn(
    variantSize[size] ?? 'size-0',
    'cursor-default',
    'flex items-center justify-center',
    'overflow-hidden',
    className
  );

  return (
    <div className={containerClassName}>
      {/* translate layer = pixel-true nudge */}
      <div
        data-translate-layer
        style={{ transform: `translateX(${tx}rem) translateY(${ty}rem)` }}
      >
        {/* scale layer = optical sizing */}
        <div
          data-scale-layer
          className={cn(iconSize, 'flex')}
          style={{
            transformOrigin: '50% 50%',
            transform: `scale(${scale})`,
          }}
        >
          <Icon strokeWidth={strokeWidth} className={iconSize} />
        </div>
      </div>
    </div>
  );
};

export interface IconButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants>,
    IconFrameProps {
  icon: LBIcon;
  asChild?: boolean;
  size?: IconButtonSize;
  tooltip?: string;
  tooltipOptions?: Omit<TooltipProps, 'label' | 'children'>;
  isLoading?: boolean;
}

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      variant,
      size = 'default',
      icon,
      asChild = false,
      tooltip,
      tooltipOptions,
      isLoading = false,
      tx = 0,
      ty = 0,
      scale = 1,
      strokeWidth,
      ...props
    },
    ref
  ) => {
    const iconSize = useIconSize(size);

    const renderIcon = () => {
      if (isLoading)
        return (
          <IconFrame
            size={size}
            variant={variant}
            icon={Icons.loader}
            tx={tx}
            ty={ty}
            scale={scale}
            strokeWidth={strokeWidth}
            className={cn('text-inherit animate-spin', iconSize)}
            aria-label="Loading"
          />
        );

      return (
        <IconFrame
          size={size}
          variant={variant}
          icon={icon}
          tx={tx}
          ty={ty}
          scale={scale}
          strokeWidth={strokeWidth}
        />
      );
    };

    const clsName = cn(
      iconButtonVariants({ variant, size }),
      'cursor-default',
      'flex items-center justify-center',
      'overflow-hidden',
      className
    );

    // Tooltips never show on touch, so the accessible name cannot rely on them
    const ariaLabel = props['aria-label'] ?? tooltip;

    const buttonContent = asChild ? (
      <SlotPrimitive.Slot
        ref={ref}
        {...props}
        aria-label={ariaLabel}
        className={clsName}
      >
        {renderIcon()}
      </SlotPrimitive.Slot>
    ) : (
      <button
        aria-busy={isLoading}
        ref={ref}
        {...props}
        aria-label={ariaLabel}
        className={clsName}
      >
        {renderIcon()}
      </button>
    );

    if (tooltip) {
      return (
        <Tooltip label={tooltip} {...tooltipOptions}>
          <span
            className={cn(props.disabled && 'cursor-not-allowed')}
            aria-disabled={props.disabled}
          >
            {buttonContent}
          </span>
        </Tooltip>
      );
    }

    return buttonContent;
  }
);

IconButton.displayName = 'IconButton';

export { IconButton, IconFrame };
